<script setup lang="ts">
import { ElNotification } from 'element-plus'
import { confirmPasswordValidator, onChangePassword, passwordValidator } from '@/compositions/validators'
import { ModeEnum } from '@/types/enums'

const { t } = useI18n()
const loading = ref(false)
const { updateMode, resetPasswordEmail } = siteUtils
function handleChangePasswordResponse(status: string): void {
  switch (status) {
    case 'ok':
      ElNotification({
        title: t('resetPassword.notify.changed.title'),
        message: t('resetPassword.notify.changed.message'),
        type: 'success',
        duration: 9000,
        offset: 100,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
      resetPasswordEmail.value = ''
      updateMode(ModeEnum.login)
      break
    case 'invalid_token':
      ElNotification({
        title: t('resetPassword.notify.invalidToken.title'),
        type: 'error',
        duration: 9000,
        offset: 100,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
      break
    default:
      ElNotification({
        title: t('resetPassword.notify.unavailable.title'),
        message: t('resetPassword.notify.unavailable.message'),
        type: 'error',
        duration: 9000,
        offset: 100,
        appendTo: '#auth-wrapper',
        customClass: 'right-1/2 translate-x-1/2 large absolute'
      })
  }
}
function onSubmit(data: FormTypes.SubmitForm) {
  loading.value = true
  const { getGoatApiUrl } = siteUtils
  const { getAxiosInstance, catchFormErrors } = appUtilsPlugin
  ElNotification.closeAll()
  getAxiosInstance()
    ?.post(getGoatApiUrl('reset-password-confirm'), { ...data.form, email: resetPasswordEmail.value || '' })
    .then((response) => {
      handleChangePasswordResponse(response?.data?.status || '')
    })
    .catch(catchFormErrors(data.externalErrors, { appendTo: '#auth-wrapper' }))
    .then(() => {
      loading.value = false
    })
}
</script>

<template>
  <div class="box-form-wrapper mx-auto max-w-2xl big:max-w-(--breakpoint-xl) big:px-16 mt-5">
    <div
      class="box-form big:mt-10 mx-3 bg-white pt-10 big:pt-16 pb-10 px-2 big:px-16 rounded-2xl flex flex-col items-center"
    >
      <div class="mx-auto w-full max-w-md">
        <h1 class="text-lg big:text-2xl uppercase font-semibold text-center tracking-wider ">
          {{ t('resetPassword.title') }}
        </h1>
        <goat-form-component
          class="mt-6 big:mb-5 w-full"
          size="large"
          hide-required-asterisk
          @submit="onSubmit"
        >
          <template #default="{ submit, form, formRefer }">
            <goat-form-field-text-trim
              api-field-name="password"
              :placeholder="t('form.password')"
              :label="!!form?.password?.length ? t('form.password') : false"
              class="mt-1 big:mt-1.5 text-lg"
              type="password"
              show-password
              :rules="[passwordValidator()]"
              @input="onChangePassword(form, formRefer)"
              @keyup.enter.stop.prevent="submit"
            />
            <goat-form-field-text-trim
              api-field-name="confirmPassword"
              :placeholder="t('form.confirmPassword')"
              :label="!!form?.confirmPassword?.length ? t('form.confirmPassword') : false"
              class="mt-1 big:mt-1.5 text-lg"
              type="password"
              show-password
              :rules="[confirmPasswordValidator(form)]"
              @keyup.enter.stop.prevent="submit"
            />
            <goat-form-field-text-trim
              api-field-name="token"
              :placeholder="t('form.verificationCode')"
              :label="!!form?.token?.length ? t('form.verificationCode') : false"
              class="mt-1 big:mt-1.5 text-lg"
              required
              maxlength="4"
              show-word-limit
              :rules="[{ type: 'string', trigger: 'change', len: 4, message: t('form.validation.fourCharsRequired') }]"
              @keyup.enter.stop.prevent="submit"
            />
            <goat-form-fields-group
              justify="space-between"
            >
              <el-button
                type="info"
                class="text-white rounded-lg uppercase text-lg w-[46%]"
                @click="updateMode(ModeEnum.login)"
              >
                {{ t('resetPassword.cancelBtn') }}
              </el-button>
              <el-button
                type="primary"
                class="text-white rounded-lg uppercase text-lg w-[46%]"
                size="large"
                :loading="loading"
                @click="submit()"
              >
                {{ t('resetPassword.confirmBtn') }}
              </el-button>
            </goat-form-fields-group>
          </template>
        </goat-form-component>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
</style>
